import React, { useState, useRef, useMemo, useEffect } from 'react';
import { ToastType, Vendor, ActiveListItem } from '../../../types/types';
import { parseTimeString } from '../../../lib/mapUtils';
import { format } from 'date-fns';
import VendorCard from '../../Cards/VendorCard';
import CarouselNavigation from './CarouselNavigation';

interface EventCarouselProps {
  vendors: Vendor[];
  currentDate: Date;
  selectedDisplayIds?: string[];
  onEventSelect: (vendorId: number, displayId: string) => void;
  isPopup?: boolean;
  initiallyExpanded?: boolean;
  isMobile?: boolean;
  onSignUpModalRequest?: (headerText: string) => void;
  onShareEvent?: (vendorId: number, date: Date, coordinates: [number, number]) => void;
  showToast?: (message: string, type?: ToastType, duration?: number, dismissible?: boolean) => void;
  activeListItems?: ActiveListItem[];
  onPinEvent?: (
    vendorId: number,
    eventDate: string,
    vendorName: string,
    vendorCategory: string
  ) => Promise<number | null>;
  onUnpinEvent?: (itemId: number) => Promise<boolean>;
  onEditEvent?: (vendorId: number) => void;
}

const EventCarousel: React.FC<EventCarouselProps> = ({
  vendors,
  currentDate,
  selectedDisplayIds,
  onEventSelect,
  isPopup = true,
  initiallyExpanded = false,
  isMobile = false,
  onSignUpModalRequest,
  onShareEvent,
  showToast,
  activeListItems,
  onPinEvent,
  onUnpinEvent,
  onEditEvent,
}) => {
  const lastNavigationTime = useRef(0);
  const NAVIGATION_DEBOUNCE_MS = 300;

  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);

  // Reset expansion when selection is cleared
  useEffect(() => {
    if (!selectedDisplayIds || selectedDisplayIds.length === 0) {
      setIsExpanded(false);
    }
  }, [selectedDisplayIds]);

  const dateFilteredEvents = useMemo(() => {
    return vendors
      .filter(vendor => {
        const jsDay = currentDate.getDay();
        const dbDay = jsDay === 0 ? 7 : jsDay;
        return vendor.schedule.days.includes(dbDay);
      })
      .sort((a, b) => {
        const aTime = parseTimeString(a.localized_schedule?.start_time || a.schedule.start_time);
        const bTime = parseTimeString(b.localized_schedule?.start_time || b.schedule.start_time);
        return aTime - bTime;
      });
  }, [vendors, currentDate]);

  const displayDate = useMemo(() => format(currentDate, 'yyyy-MM-dd'), [currentDate]);

  const currentIndex = useMemo(() => {
    if (!selectedDisplayIds || selectedDisplayIds.length === 0) {
      return 0;
    }

    const selectedIndex = dateFilteredEvents.findIndex(vendor => {
      const vendorDisplayId = `${vendor.id}-${displayDate}`;
      return selectedDisplayIds.includes(vendorDisplayId);
    });

    return selectedIndex !== -1 ? selectedIndex : 0;
  }, [selectedDisplayIds, dateFilteredEvents, displayDate]);

  const totalEvents = dateFilteredEvents.length;
  const currentEvent = dateFilteredEvents[currentIndex];

  if (totalEvents === 0 || !currentEvent) {
    return null;
  }

  const expandedVendor = {
    ...currentEvent,
    displayDate: currentDate,
    displayID: `${currentEvent.id}-${displayDate}`,
  };

  const handlePrevious = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const now = Date.now();
    if (now - lastNavigationTime.current < NAVIGATION_DEBOUNCE_MS) {
      return;
    }
    lastNavigationTime.current = now;

    const newIndex = currentIndex > 0 ? currentIndex - 1 : totalEvents - 1;
    const newEvent = dateFilteredEvents[newIndex];

    if (newEvent) {
      const newDisplayId = `${newEvent.id}-${displayDate}`;
      onEventSelect(newEvent.id, newDisplayId);
    }
  };

  const handleNext = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const now = Date.now();
    if (now - lastNavigationTime.current < NAVIGATION_DEBOUNCE_MS) {
      return;
    }
    lastNavigationTime.current = now;

    const newIndex = currentIndex < totalEvents - 1 ? currentIndex + 1 : 0;
    const newEvent = dateFilteredEvents[newIndex];

    if (newEvent) {
      const newDisplayId = `${newEvent.id}-${displayDate}`;
      onEventSelect(newEvent.id, newDisplayId);
    }
  };

  const handleCardClick = (e?: React.MouseEvent | React.TouchEvent) => {
    // Prevent event bubbling if event is provided
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    // Toggle expansion when card is clicked directly
    setIsExpanded(!isExpanded);
    onEventSelect(currentEvent.id, expandedVendor.displayID);
  };

  const showNavigation = totalEvents > 1;

  // Mobile-responsive styling - simpler approach
  const containerStyle = {
    position: 'relative' as const,
    padding: isMobile ? '8px' : '18px',
    transformOrigin: 'top left' as const,
    width: '100%',
    maxWidth: isMobile ? '280px' : '320px',
    margin: '0 auto',
    zIndex: 1000,
  };

  const flexContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center', // Changed from 'space-between' to 'center'
    gap: isMobile ? '6px' : '8px', // Small gap between all elements
    width: '100%',
    minHeight: '44px',
  };

  const cardContainerStyle: React.CSSProperties = {
    flex: '0 1 auto',
    cursor: 'pointer',
    minWidth: 0,
    margin: 0,
    maxWidth: isMobile ? '200px' : '240px',
    ...(isMobile && {
      touchAction: 'manipulation',
      userSelect: 'none',
    }),
  };

  const indicatorContainerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: isMobile ? '8px' : '12px', // Changed from marginTop to marginBottom
    gap: isMobile ? '4px' : '6px',
    padding: '6px 12px', // Add padding around dots
    background: 'rgba(255, 255, 255, 0.15)', // Subtle dark backdrop
    borderRadius: '12px', // Rounded pill shape
    width: 'fit-content', // Only as wide as needed
    margin: '0 auto', // Center it
  };

  return (
    <div style={containerStyle}>
      {showNavigation && (
        <div style={indicatorContainerStyle}>
          {Array.from({ length: totalEvents }, (_, index) => (
            <div
              key={index}
              style={{
                width: index === currentIndex ? '6px' : '4px',
                height: index === currentIndex ? '6px' : '4px',
                borderRadius: '50%',
                backgroundColor:
                  index === currentIndex ? 'var(--color-highlight)' : 'var(--color-border-strong)',
                transition: 'background-color 0.2s ease',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)', // Add shadow to each dot
              }}
            />
          ))}
        </div>
      )}
      <div style={flexContainerStyle}>
        {showNavigation && (
          <CarouselNavigation direction="previous" onClick={handlePrevious} disabled={false} />
        )}

        <div
          key={expandedVendor.displayID}
          style={cardContainerStyle}
          onClick={handleCardClick}
          // Add touch event handlers for mobile
          onTouchEnd={isMobile ? handleCardClick : undefined}
        >
          <VendorCard
            vendor={expandedVendor}
            isSelected={isExpanded}
            onClick={handleCardClick}
            isPopup={isPopup}
            onSignUpModalRequest={onSignUpModalRequest}
            onShareEvent={onShareEvent}
            showToast={showToast}
            activeListItems={activeListItems}
            onPinEvent={onPinEvent}
            onUnpinEvent={onUnpinEvent}
            onEditEvent={onEditEvent}
          />
        </div>

        {showNavigation && (
          <CarouselNavigation direction="next" onClick={handleNext} disabled={false} />
        )}
      </div>
    </div>
  );
};

export default EventCarousel;
